import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { QueryClientProvider } from '@tanstack/react-query';
import { AgChartsEnterpriseModule } from 'ag-charts-enterprise';
import { ModuleRegistry } from 'ag-grid-community';
import {
  AllEnterpriseModule,
  CellSpanModule,
  ColumnMenuModule,
  ColumnsToolPanelModule,
  ContextMenuModule,
  IntegratedChartsModule,
  LicenseManager,
  PaginationModule,
  ServerSideRowModelModule
} from 'ag-grid-enterprise';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import {
  closeSnackbar,
  SnackbarProvider,
  type SnackbarProviderProps
} from 'notistack';
import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider
} from 'react-router';

import useDatadog from './hooks/useDatadog';
import { Routes } from './routes';
import { queryClient } from './services/BaseApi.service';
import theme from './theme';

dayjs.extend(duration);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(utc);

ModuleRegistry.registerModules([
  AllEnterpriseModule,
  PaginationModule,
  ColumnsToolPanelModule,
  ColumnMenuModule,
  ContextMenuModule,
  ServerSideRowModelModule,
  CellSpanModule,
  IntegratedChartsModule.with(AgChartsEnterpriseModule)
]);

if (import.meta.env.MODE === 'development') {
  const licenseKey = import.meta.env.VITE_AG_GRID_LICENSE_KEY;
  const maskedKey = import.meta.env.VITE_AG_GRID_LICENSE_KEY
    ? `${licenseKey.slice(0, 4)}****${licenseKey.slice(-4)}`
    : 'No key found!';

  console.warn('AG Charts License Key Loaded:', maskedKey);
}

LicenseManager.setLicenseKey(import.meta.env.VITE_AG_GRID_LICENSE_KEY || '');

const snackbarProviderProps: SnackbarProviderProps = {
  autoHideDuration: 10000,
  anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
  action: (snackbarId) => (
    <IconButton
      onClick={() => closeSnackbar(snackbarId)}
      sx={{ color: 'text.contrast' }}
    >
      <FontAwesomeIcon icon={faTimes} />
    </IconButton>
  )
};

export const router = createBrowserRouter(createRoutesFromElements(Routes()));

function App() {
  // Disable memoization for this component. Do not remove
  'use no memo';
  // Initialize Datadog
  useDatadog();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline>
        <QueryClientProvider client={queryClient}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <SnackbarProvider {...snackbarProviderProps}>
              <RouterProvider router={router} />
            </SnackbarProvider>
          </LocalizationProvider>
        </QueryClientProvider>
      </CssBaseline>
    </ThemeProvider>
  );
}

export default App;
